import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiPost as useApiV2Post } from '@sierralabs/core-ui';

import { StateNode, StateNodeType, Workflow } from '@sierralabs/workflow';

import api, { ApiParams, FetchError } from '../../common/api/api';
import {
  ApiResult,
  FetchApiCallback,
  useApiGet,
  useApiPatch,
  useApiPost
} from '../../common/api/Api.hooks';
import { VersionItem } from '../../common/version-item/Version.interface';
import { StatusColor } from '../../common/ColorSelector';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { useUserContext } from '../../user/User.context';
import { FileDto } from '../documents/Document.interface';
import {
  ProductEntity,
  ProductTaskTypeEntities,
  ProductWorkflowEntities
} from '../products/Product.interface';
import { TaskType, TaskTypeEntity } from '../task-types/TaskType.interface';
import { CreateSubTasksPlugin } from '../workflow/plugins/services/CreateSubTasksPlugin';
import { CheckDependenciesParams } from '../workflow/plugins/transition-conditions/CheckDependenciesPlugin';
import { CheckSubTasksPlugin } from '../workflow/plugins/transition-conditions/CheckSubTasksPlugin';
import { CheckDependenciesLegacyParams } from '../workflow/plugins-v2/transition-conditions/CheckDependenciesLegacyPlugin';
import {
  useStateNodeByStatus,
  useTaskWorkflow
} from '../workflow/Workflow.hooks';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import {
  compareTaskStatus,
  isSubTaskInActiveStatePath,
  parseTaskStatus
} from './task.helper';
import {
  TaskBulkEventGroup,
  CheckDependenciesResult,
  CreateSubTasksResult,
  SubTaskUser,
  Task,
  TaskAuditLog,
  TaskBulkEvent,
  TaskEntity,
  TaskListItem,
  TaskStatusLog
} from './Task.interface';
import {
  CheckSubTasksParams,
  CreateSubTaskParams,
  useGetWorkflowMachineState
} from './Task.machine';
import {
  Hazard,
  HazardEntity,
  HazardTaskTypeEntities,
  HazardWorkflowEntities
} from '../hazards/Hazard.interface';
import { apiResolve } from '../../common/api/apiResolve';
import { getEntityPrefix } from '../../common/entityId.hooks';

export function useGetTaskStatusLogs(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<TaskStatusLog>
  >(`tasks/${id}/status-log`);
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return {
    taskStatusLogs: data,
    isLoading,
    loadingError: error,
    getStatusLogs: get
  };
}

export function useGetVersionedTaskStatusLogs(id: number) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<TaskStatusLog>
  >(`tasks/${id}/status-log/versions`);
  return {
    taskStatusLogList: data,
    isLoading,
    loadingError: error,
    getStatusLogs: get
  };
}

export function useFindTask(id: number) {
  const { data, isLoading, error, get } = useApiGet<Task>(`tasks/${id}`);
  return {
    task: data,
    isLoading: isLoading,
    findError: error,
    findTask: get
  };
}

export function useFindTaskList() {
  const { data, get, ...result } =
    useApiGet<PaginatedList<TaskListItem>>('tasks/list');
  return { ...result, taskList: data, findTasks: get };
}

export function useFindTaskFiles(id: number) {
  const { data, isLoading, error, get } = useApiGet<FileDto[]>(
    `tasks/${id}/files`
  );
  return {
    taskFiles: data,
    isLoading,
    findError: error,
    findTaskFiles: get
  };
}

export function useFindTaskFileVersions(id: number) {
  const { data, isLoading, error, get } = useApiGet<VersionItem[]>(
    `tasks/${id}/file-versions`
  );
  return { files: data, isLoading, error, findFiles: get };
}

export function useAssignTaskFileVersions(id: number) {
  const { data, isLoading, error, post } = useApiPost<Partial<Task>>(
    `tasks/${id}/file-versions`
  );
  const assign = useCallback(
    (files: { id: number; version: number }[]) => post({ files }),
    [post]
  );
  return { task: data, isLoading, error, assign };
}

// non-hook implementation
export function updateTask(id: number, params: ApiParams) {
  const request = api.patch(`tasks/${id}`, params);
  return { request, result: apiResolve<Task>(request) };
}

export function useUpdateTask(id?: number) {
  const [path, setPath] = useState('');
  const [apiParams, setApiParams] = useState<ApiParams>();
  const { data, isLoading, error, patch } = useApiPatch<Task>(
    path || `tasks/${id}`
  );
  useEffect(() => {
    if (path && apiParams) {
      patch(apiParams);
    }
  }, [path, apiParams, patch]);
  const updateTaskById = useCallback((id: number, apiParams: ApiParams) => {
    setPath(`tasks/${id}`);
    setApiParams(apiParams);
  }, []);
  return {
    updatedTask: data,
    isUpdating: isLoading,
    updateError: error,
    updateTask: patch,
    updateTaskById
  };
}

export function useGetTaskAuditLog(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<TaskAuditLog>
  >(`tasks/${id}/audit-log`);
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return {
    taskAuditLogList: data,
    isLoading,
    loadingError: error,
    getAuditLog: get
  };
}

export function useTaskStateType(
  task?: Task,
  status?: string
): { isFinal: boolean } {
  const [isFinal, setIsFinal] = useState<boolean>(false);
  const workflow: Workflow | undefined = useTaskWorkflow(task);
  const stateNode: StateNode | undefined = useStateNodeByStatus(
    task?.status || status,
    workflow
  );

  useEffect(() => {
    setIsFinal(stateNode?.type === StateNodeType.Final);
  }, [stateNode]);

  return { isFinal };
}

export function useFindTaskByEntity(entity: WorkflowEntity) {
  const { data, isLoading, error, get } = useApiGet<Task[]>(
    `tasks/entity/${entity}`
  );
  return { data, isLoading, error, get };
}

export function useGetTaskEntity(
  taskId: number | undefined,
  shouldFetchOnMount: boolean = true
): [
  TaskEntity | undefined,
  ApiResult<TaskEntity | undefined>,
  FetchApiCallback
] {
  const [taskEntities, setTaskEntities] = useState<TaskEntity[]>([]);
  const { get, ...result } = useApiGet<TaskEntity | undefined>(
    `tasks/${taskId || 0}/task-entity`
  );
  const { data } = result;
  // current taskEntity based on taskId
  const taskEntity: TaskEntity | undefined = useMemo(() => {
    return taskEntities.find(
      (taskEntity: TaskEntity) => taskEntity.taskId === taskId
    );
  }, [taskId, taskEntities]);

  useEffect(() => {
    // fetch when taskId changes
    if (shouldFetchOnMount && typeof taskId === 'number') {
      get();
    }
  }, [get, taskId, shouldFetchOnMount]);

  // update taskEntities on fetch
  useEffect(() => {
    if (!data) {
      return;
    }
    const newTaskEntities: TaskEntity[] = [...taskEntities];
    // replace if entity exists
    const existingIndex: number = taskEntities.findIndex(
      (taskEntity: TaskEntity) =>
        taskEntity.id === data.id && taskEntity.taskId === data.taskId
    );
    if (existingIndex > -1) {
      newTaskEntities.splice(existingIndex, 1, data);
    } else {
      newTaskEntities.push(data);
    }
    setTaskEntities(newTaskEntities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return [taskEntity, result, get];
}

export interface TaskStatusColorHookProps {
  task?: Pick<Task, 'status' | 'workflowId' | 'taskTypeName'>;
  status?: string;
  getLabel?: (label: string) => string;
}

export function useTaskStatusColor(props: TaskStatusColorHookProps) {
  const { task, getLabel } = props;
  // get status from task or task log (props.status)
  const status: string = props.status || task?.status || '';
  const workflow: Workflow | undefined = useTaskWorkflow(task);
  const stateNode: StateNode | undefined = useStateNodeByStatus(
    status,
    workflow
  );
  const taskStatus: string = parseTaskStatus(status || stateNode?.name || '');
  const stateColor: any | undefined = stateNode?.meta?.color;
  const color: StatusColor =
    stateColor && typeof stateColor === 'object'
      ? stateColor
      : { color: '#333', background: '#efefef' };

  const label: string = useMemo((): string => {
    const label: string = taskStatus || 'Unknown Status';
    return getLabel?.(label) || label;
  }, [getLabel, taskStatus]);

  return { color, label };
}

export interface TaskRedirectOptions {
  taskId: number;
  targetEntity?: string;
  taskEntity?: TaskEntity;
}

/**
 * Uses `task.taskType.targetEntity` instead of the deprecated `task.type`
 * to get the Task redirect path.
 */
export async function getTaskPath(
  options: TaskRedirectOptions
): Promise<{ path?: string; error?: FetchError } | undefined> {
  const { taskId, targetEntity } = options;
  if (!targetEntity) {
    return;
  }

  switch (targetEntity) {
    case TaskTypeEntity.File:
      return { path: `/portal/documents/file/task/${taskId}` };
    case TaskTypeEntity.Training:
      return { path: `/portal/admin/training/task/${taskId}` };
    // common path
    default:
      const pathKeyMap: Record<string, string> = {
        [TaskTypeEntity.Capa]: 'capa',
        [TaskTypeEntity.Scar]: 'scar',
        [TaskTypeEntity.ChangeOrder]: 'change-order',
        [TaskTypeEntity.Risk]: 'risks',
        [TaskTypeEntity.Spor]: 'spors',
        [TaskTypeEntity.Supplier]: 'suppliers'
      };
      const pathKey = pathKeyMap[targetEntity];
      if (pathKey) {
        return { path: `/portal/${pathKey}/task/${taskId}` };
      }
  }

  // for products and hazards, get task entity
  // since their routes use the entity ids instead of task ids
  const isProduct = targetEntity === TaskTypeEntity.Product;
  const isProductEntity = (ProductTaskTypeEntities as string[]).includes(
    targetEntity
  );
  const isHazard = targetEntity === TaskTypeEntity.Hazard;
  const isHazardEntity = (HazardTaskTypeEntities as string[]).includes(
    targetEntity
  );

  if (!isProduct && !isProductEntity && !isHazard && !isHazardEntity) {
    return;
  }

  // get task entity if not provided
  let { taskEntity } = options;
  if (!taskEntity) {
    const request = api.get(`tasks/${taskId}/task-entity`, {});
    const { data, error } = await apiResolve<TaskEntity>(request);
    if (data) {
      taskEntity = data;
    } else {
      return { error };
    }
  }

  if (isProduct) {
    return { path: `/portal/products/${taskEntity.id}` };
  } else if (isProductEntity) {
    const pathKeyMap: Record<string, string> = {
      [TaskTypeEntity.UserNeed]: 'user-needs',
      [TaskTypeEntity.DesignInput]: 'design-inputs',
      [TaskTypeEntity.DesignOutput]: 'design-outputs',
      [TaskTypeEntity.DesignValidation]: 'validations',
      [TaskTypeEntity.DesignVerification]: 'verifications',
      [TaskTypeEntity.DesignSpec]: 'design-specs'
    };
    const pathKey = pathKeyMap[targetEntity];
    if (pathKey) {
      const { id, productId } = taskEntity as ProductEntity;
      return { path: `/portal/products/${productId}/${pathKey}/${id}` };
    }
  } else if (isHazard) {
    const { id, productId } = taskEntity as Hazard;
    return { path: `/portal/products/${productId}/hazards/${id}` };
  } else if (isHazardEntity) {
    const pathKeyMap: Record<string, string> = {
      [TaskTypeEntity.Cause]: 'causes',
      [TaskTypeEntity.Control]: 'controls',
      [TaskTypeEntity.Action]: 'actions'
    };
    const pathKey = pathKeyMap[targetEntity];
    if (pathKey) {
      const { id, hazardId, hazard } = taskEntity as HazardEntity;
      return {
        path: `/portal/products/${hazard.productId}/hazards/${hazardId}/${pathKey}/${id}`
      };
    }
  }
}

/**
 * NOTE: This implementation uses the deprecated `task.type`.
 * @deprecated Use {@linkcode getTaskPath} instead.
 */
export function useTaskRedirect(task: Task | undefined): {
  path?: string;
  redirect?: string;
  result: ApiResult<TaskEntity | undefined>;
  getTaskEntity: FetchApiCallback;
} {
  const [redirect, setRedirect] = useState<string | undefined>();

  // handle empty task.type for subtasks
  const taskId: number | undefined = useMemo(() => {
    if (!task) {
      return undefined;
    }
    return task.type ? task.id : task.parent?.id || task.id;
  }, [task]);

  const taskType: WorkflowEntity | undefined = useMemo(() => {
    if (!task) {
      return undefined;
    }
    return task.type || task.parent?.type;
  }, [task]);

  const isProductEntity: boolean = useMemo(() => {
    return !!taskType && ProductWorkflowEntities.includes(taskType);
  }, [taskType]);

  const isHazardEntity: boolean = useMemo(() => {
    return !!taskType && HazardWorkflowEntities.includes(taskType);
  }, [taskType]);

  const taskIdForTaskEntity: number | undefined = useMemo(() => {
    // if task type is a product or hazard entity, fetch task entity
    return isProductEntity || isHazardEntity ? taskId : undefined;
  }, [taskId, isProductEntity, isHazardEntity]);

  const [taskEntity, result, get] = useGetTaskEntity(
    taskIdForTaskEntity,
    false
  );

  const getTaskEntity: FetchApiCallback = useCallback(
    (params?: ApiParams) => {
      if (isProductEntity || isHazardEntity) {
        get(params);
      }
    },
    [get, isProductEntity, isHazardEntity]
  );

  const path: string | undefined = useMemo(() => {
    const id: number | undefined = taskId;
    if (typeof id !== 'number' || !taskType) {
      return undefined;
    }

    switch (taskType) {
      case WorkflowEntity.File:
        return `/portal/documents/file/task/${id}`;
      case WorkflowEntity.Training:
        return `/portal/admin/training/task/${id}`;
      // singular common
      case WorkflowEntity.Capa:
      case WorkflowEntity.Scar:
      case WorkflowEntity.ChangeOrder:
        return `/portal/${taskType}/task/${id}`;
      // plural common
      case WorkflowEntity.Risk:
      case WorkflowEntity.Spor:
      case WorkflowEntity.Supplier:
        return `/portal/${taskType}s/task/${id}`;
    }

    if (taskEntity) {
      // product entity
      if (isProductEntity) {
        const { id: eId, productId } = taskEntity as ProductEntity;
        if (productId) {
          switch (taskType) {
            case WorkflowEntity.DesignVerification:
              return `/portal/products/${productId}/verifications/${eId}`;
            case WorkflowEntity.DesignValidation:
              return `/portal/products/${productId}/validations/${eId}`;
          }
          return `/portal/products/${productId}/${taskType}s/${eId}`;
        }
        // hazard entity
      } else if (isHazardEntity) {
        const { id: eId, hazardId, hazard } = taskEntity as HazardEntity;
        return `/portal/products/${hazard.productId}/hazards/${hazardId}/${taskType}s/${eId}`;
      }
    }

    return undefined;
  }, [taskId, taskType, taskEntity, isProductEntity]);

  useEffect(() => {
    if ((isProductEntity || isHazardEntity) && path && taskEntity) {
      setRedirect(path);
    }
  }, [path, taskType, taskEntity, isProductEntity, isHazardEntity]);

  return { path, redirect, result, getTaskEntity };
}

export function useCheckDependencies(taskId: number) {
  const { post, ...result } = useApiPost<CheckDependenciesResult>(
    `tasks/${taskId}/check-dependencies`
  );
  const checkDependencies = useCallback(
    (params: CheckDependenciesParams) => post(params),
    [post]
  );
  return { ...result, checkDependencies };
}

// v2 task check with v1 entities
export function useCheckDependenciesLegacy(taskV2Id: number) {
  const { post, ...result } = useApiV2Post<CheckDependenciesResult>(
    `tasks/plugins/check-dependencies-legacy/${taskV2Id}`
  );
  const checkDependencies = useCallback(
    (params: CheckDependenciesLegacyParams) => post(params),
    [post]
  );
  return { ...result, checkDependencies };
}

export function useCreateSubTasks(taskId: number) {
  const { post, ...result } = useApiPost<CreateSubTasksResult>(
    `tasks/${taskId}/subtasks`
  );
  const createSubTasks = useCallback(
    (params: { subTaskUsers: SubTaskUser[] }) => post(params),
    [post]
  );
  return { ...result, createSubTasks };
}

export function useReassignSubTasks(task: Task) {
  const { user } = useUserContext();
  const workflow = useTaskWorkflow(task);
  const stateNode = useStateNodeByStatus(task.status, workflow);
  const { taskStatusLogs, getStatusLogs } = useGetTaskStatusLogs(task.id!);
  const isAdmin = (user?.roles || []).some(
    role => role.name && ['Admin', 'SiteAdmin'].includes(role.name)
  );
  const isAssigned = !!(user && task.user && task.user.id === user.id);
  const hasPermission = isAdmin || isAssigned;
  const statusLog = taskStatusLogs?.data?.[0];
  const didTransition = !!(
    user &&
    statusLog &&
    statusLog.createdBy.id === user.id &&
    compareTaskStatus(statusLog.status, task.status)
  );

  // groups from createSubTasks action
  const groupIds = useMemo(() => {
    const groupIds: number[] = [];
    const invokes = !stateNode
      ? []
      : Array.isArray(stateNode.invoke)
      ? stateNode.invoke
      : stateNode.invoke
      ? [stateNode.invoke]
      : [];
    for (const invoke of invokes) {
      if (invoke.src === CreateSubTasksPlugin.src && invoke.data) {
        const { data } = invoke as CreateSubTaskParams;
        const ids = data.groupIds || data.groups.map(g => g.id);
        groupIds.push(...ids);
      }
    }
    return groupIds;
  }, [stateNode]);

  // final subTask paths: get paths from checkSubTasks params
  const subTaskPaths = useMemo(() => {
    const paths: string[] = [];
    for (const event of stateNode?.events || []) {
      for (const transition of event.transitions || []) {
        for (const condition of transition.conditions || []) {
          const params = condition.params as CheckSubTasksParams | undefined;
          if (
            condition.type === CheckSubTasksPlugin.type &&
            params &&
            params.path
          ) {
            paths.push(params.path);
          }
        }
      }
    }
    return paths;
  }, [stateNode]);

  // check from configured paths instead of workflow final state type
  const isSubTaskFinalized = useCallback(
    (subTask: Pick<Task, 'status'>) => {
      return subTaskPaths.some(targetPath => {
        return compareTaskStatus(targetPath, subTask.status);
      });
    },
    [subTaskPaths]
  );

  // can reassign subTask if:
  // - didTransition: user transitioned the main task and assigned subTasks
  // - hasPermission: user has admin role or is assigned to the main task
  // - subTask is not yet finalized
  // - subTask has no statePath (default behavior) or it matches the main task status
  const canReassign = useCallback(
    (subTask: Task) => {
      return (
        (didTransition || hasPermission) &&
        !isSubTaskFinalized(subTask) &&
        isSubTaskInActiveStatePath(subTask, task)
      );
    },
    [didTransition, hasPermission, task, isSubTaskFinalized]
  );

  // if no permissions, get latest status log for didTransition check
  useEffect(() => {
    !hasPermission && getStatusLogs({ limit: 1 });
  }, [hasPermission, getStatusLogs]);

  return {
    groupIds,
    didTransition,
    hasPermission,
    canReassign,
    isSubTaskFinalized
  };
}

export function useGroupTaskBulkEvents() {
  const { runMachine } = useGetWorkflowMachineState();

  const groupTaskBulkEvents = useCallback(
    async (
      taskEntities: TaskEntity[],
      options: { useDeprecatedTaskKey?: boolean } = {}
    ) => {
      const taskTypes: TaskType[] = [];
      for (const entity of taskEntities) {
        const taskType = entity.task?.taskType;
        if (taskType && !taskTypes.some(t => t.name === taskType.name)) {
          taskTypes.push(taskType);
        }
      }

      const bulkEventGroups: TaskBulkEventGroup[] = [];
      for (const taskType of taskTypes) {
        const group: TaskBulkEventGroup = { taskType, ids: [], bulkEvents: [] };
        for (const entity of taskEntities) {
          const task = entity.task;
          const taskTypeName = task?.taskType?.name || task?.taskTypeName;
          if (!task || !taskTypeName || taskTypeName !== taskType.name) {
            continue;
          }
          if (!group.prefix) {
            /**
             ** NOTE: prefix will be changed to use default taskType prefix in SLQMS-921
             */
            group.prefix = options.useDeprecatedTaskKey
              ? getEntityPrefix(entity.task!.type)
              : taskType.prefix;
          }
          /**
           ** NOTE: id will be changed to use task.key in SLQMS-921
           */
          const taskKey = options.useDeprecatedTaskKey
            ? `${getEntityPrefix(task.type)}-${entity.id}`
            : task.key;
          taskKey && group.ids.push(taskKey);

          const { stateNode } = await runMachine({ task });
          for (const event of stateNode?.events || []) {
            const bulkEvent = group.bulkEvents.find(e => {
              return e.event.id === event.id;
            });
            if (bulkEvent) {
              bulkEvent.taskEntities.push(entity);
            } else if (stateNode && event.name) {
              group.bulkEvents.push({
                event,
                stateNode,
                taskEntities: [entity]
              });
            }
          }
        }
        bulkEventGroups.push(group);
      }

      return bulkEventGroups;
    },
    [runMachine]
  );

  return groupTaskBulkEvents;
}
